import React, { useState, useMemo, useDeferredValue, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import TransferDetailsForm from "./TransferDetailsForm";
import OnSuccess from "../../common/commonUtils/OnSuccess";
import "./TransferRegistration.css";
import SearchListStep from "./SearchListStep";
import ChoosePersonOption from "./ChoosePersonOption";
import TransferSummary from "./TransferSummary";
import PersonForm from "../../common/userRegistration/PersonForm";
import { FadeLoader } from "react-spinners";
import { UsersApi, CustomersApi, TransactionsApi } from "../../../api/common/apiIndex";
import { useQuery } from "@tanstack/react-query";
import CountrySelect from "../../common/commonUtils/CountrySelect";
import TransferSidebar from "./TransferSidebar";

countries.registerLocale(enLocale);

const toAlpha2 = (val) => {
  if (!val) return null;
  const trimmed = String(val).trim();
  if (trimmed.length === 2) return trimmed.toUpperCase();
  const iso = countries.getAlpha2Code(trimmed, "en");
  return iso ? iso.toUpperCase() : null;
};

const fromAlpha2 = (code) => {
  const iso = toAlpha2(code);
  if (!iso) return null;
  try {
    return countries.getName(iso, "en") || iso;
  } catch {
    return iso;
  }
};

const getPrevStep = (step, senderType, recipientType) => {
  switch (step) {
    case 2: return 1;
    case 3: return 2;
    case 4: return 2;
    case 5: return senderType === "existing" ? 3 : 4;
    case 6: return 5;
    case 7: return 5;
    case 8: return recipientType === "existing" ? 6 : 7;
    case 9: return 8;
    case 10: return 9;
    default: return Math.max(1, step - 1);
  }
};

const EMPTY = Object.freeze([]);
const SS_KEY = "transferWizardState";

const loadSavedWizardState = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error("Failed to parse transfer wizard state", err);
    return null;
  }
};

const normalizeStoredEntity = (entity, fallbackCode) => {
  if (!entity) return null;
  const code = toAlpha2(entity.country || fallbackCode);
  return {
    ...entity,
    country: code,
    countryName: entity.countryName || (code ? fromAlpha2(code) : entity.countryName),
  };
};

const wizardStages = [
  { label: "Countries", step: 1 },
  { label: "Sender", step: 2 },
  { label: "Recipient", step: 5 },
  { label: "Transfer", step: 8 },
  { label: "Summary", step: 9 },
  { label: "Done", step: 10 },
];

const TransferRegistration = () => {
  const navigate = useNavigate();

  const [prefillParams] = useState(() => {
    if (typeof window === "undefined") return { recipientId: null, txUuid: null };
    const sp = new URLSearchParams(window.location.search);
    return {
      recipientId: sp.get("recipientId"),
      txUuid: sp.get("prefillFromTx"),
    };
  });

  const savedState = useMemo(loadSavedWizardState, []);

  const [step, setStep] = useState(() => {
    if (typeof window === "undefined") return savedState?.step || 1;
    const sp = new URLSearchParams(window.location.search);
    const urlStep = Number(sp.get("step") || 0);
    return urlStep || savedState?.step || 1;
  });
  const setStepAndUrl = useCallback((next) => {
    setStep(next);
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    sp.set("step", String(next));
    window.history.replaceState({}, "", `${window.location.pathname}?${sp.toString()}`);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const current = Number(sp.get("step") || 0);
    if (current !== step) {
      sp.set("step", String(step));
      window.history.replaceState({}, "", `${window.location.pathname}?${sp.toString()}`);
    }
  }, [step]);

  const initialSenderCountryCode =
    savedState?.senderCountryCode ?? toAlpha2(savedState?.senderCountryName) ?? null;
  const initialSenderCountryName =
    savedState?.senderCountryName ?? (initialSenderCountryCode ? fromAlpha2(initialSenderCountryCode) : null);
  const initialRecipientCountryCode =
    savedState?.recipientCountryCode ?? toAlpha2(savedState?.recipientCountryName) ?? null;
  const initialRecipientCountryName =
    savedState?.recipientCountryName ?? (initialRecipientCountryCode ? fromAlpha2(initialRecipientCountryCode) : null);

  const [senderCountryCode, setSenderCountryCode] = useState(initialSenderCountryCode);
  const [senderCountryName, setSenderCountryName] = useState(initialSenderCountryName);

  const [recipientCountryCode, setRecipientCountryCode] = useState(initialRecipientCountryCode);
  const [recipientCountryName, setRecipientCountryName] = useState(initialRecipientCountryName);

  const [senderType, setSenderType] = useState(() => savedState?.senderType || null);
  const [recipientType, setRecipientType] = useState(() => savedState?.recipientType || null);
  const [selectedSender, setSelectedSender] = useState(() =>
    normalizeStoredEntity(savedState?.selectedSender, savedState?.senderCountryCode || savedState?.senderCountryName)
  );
  const [selectedRecipient, setSelectedRecipient] = useState(() =>
    normalizeStoredEntity(savedState?.selectedRecipient, savedState?.recipientCountryCode || savedState?.recipientCountryName)
  );
  const [searchSenderTerm, setSearchSenderTerm] = useState("");
  const [searchRecipientTerm, setSearchRecipientTerm] = useState("");
  const [transferDetails, setTransferDetails] = useState(() => savedState?.transferDetails || null);
  const [transactionId, setTransactionId] = useState(() => savedState?.transactionId || 0);
  const [prefillTransfer, setPrefillTransfer] = useState(null);
  const parsedRecipientId = prefillParams.recipientId ? Number(prefillParams.recipientId) : null;
  const prefillRecipientId = Number.isFinite(parsedRecipientId) ? parsedRecipientId : null;
  const prefillTxUuid = prefillParams.txUuid || null;

  const { data: prefillRecipientData } = useQuery({
    queryKey: ["prefill-recipient", prefillRecipientId],
    queryFn: () => CustomersApi.getById(prefillRecipientId),
    enabled: !!prefillRecipientId,
  });

  const { data: prefillTransactionData } = useQuery({
    queryKey: ["prefill-transaction", prefillTxUuid],
    queryFn: () => TransactionsApi.getByUuid(prefillTxUuid),
    enabled: !!prefillTxUuid,
  });

  const prefillRecipientApplied = useRef(false);
  const prefillTxApplied = useRef(false);

  const clearWizard = useCallback(() => {
    if (typeof window === "undefined") return;
    sessionStorage.removeItem(SS_KEY);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const payload = {
      step,
      senderCountryName,
      senderCountryCode,
      recipientCountryName,
      recipientCountryCode,
      senderType,
      recipientType,
      selectedSender,
      selectedRecipient,
      transferDetails,
      transactionId,
    };
    sessionStorage.setItem(SS_KEY, JSON.stringify(payload));
  }, [
    step,
    senderCountryName, senderCountryCode,
    recipientCountryName, recipientCountryCode,
    senderType, recipientType,
    selectedSender, selectedRecipient,
    transferDetails, transactionId,
  ]);

  const deferredSenderTerm = useDeferredValue(searchSenderTerm);
  const deferredRecipientTerm = useDeferredValue(searchRecipientTerm);
  useEffect(() => {
    if (!prefillRecipientId || prefillRecipientApplied.current) return;
    if (!prefillRecipientData) return;
    const normalized = toAlpha2(prefillRecipientData.country || prefillRecipientData.countryName);
    const countryName = normalized ? fromAlpha2(normalized) : (prefillRecipientData.countryName || prefillRecipientData.country);
    setRecipientCountryCode(normalized);
    setRecipientCountryName(countryName);
    setRecipientType("existing");
    setSelectedRecipient({
      ...prefillRecipientData,
      country: normalized,
      countryName,
    });
    prefillRecipientApplied.current = true;
    if (!prefillTxUuid && step < 5) setStepAndUrl(5);
  }, [prefillRecipientId, prefillRecipientData, prefillTxUuid, step, setStepAndUrl]);

  useEffect(() => {
    if (!prefillTxUuid || prefillTxApplied.current) return;
    if (!prefillTransactionData) return;
    const tx = prefillTransactionData;
    const senderCode = toAlpha2(tx.senderCountry);
    const senderCountryName = senderCode ? fromAlpha2(senderCode) : tx.senderCountry;
    setSenderCountryCode(senderCode);
    setSenderCountryName(senderCountryName);
    setSenderType("existing");
    setSelectedSender({
      id: tx.senderId,
      uuid: tx.senderUuid,
      firstName: tx.senderFirstName || tx.senderName,
      lastName: tx.senderLastName || "",
      name: tx.senderName,
      country: senderCode,
      countryName: senderCountryName,
      phone: tx.senderPhone,
    });

    const recipientCode = toAlpha2(tx.recipientCountry);
    const recipientCountryName = recipientCode ? fromAlpha2(recipientCode) : tx.recipientCountry;
    setRecipientCountryCode(recipientCode);
    setRecipientCountryName(recipientCountryName);
    setRecipientType("existing");
    setSelectedRecipient({
      id: tx.recipientId,
      uuid: tx.recipientUuid,
      firstName: tx.recipientFirstName || tx.recipientName,
      lastName: tx.recipientLastName || "",
      name: tx.recipientName,
      country: recipientCode,
      countryName: recipientCountryName,
      phone: tx.recipientPhone,
    });

    setPrefillTransfer({
      amount: tx.sendAmount,
      receivedAmount: tx.receiveAmount,
      sendCurrency: tx.sendCurrency,
      receiveCurrency: tx.receiveCurrency,
    });

    prefillTxApplied.current = true;
    if (step < 8) setStepAndUrl(8);
  }, [prefillTxUuid, prefillTransactionData, step, setStepAndUrl]);

  const {
    data: allAgents = EMPTY,
    isLoading: agentsLoading,
    isFetching: agentsFetching,
  } = useQuery({
    queryKey: ["agents", { page: 0, size: 200, sort: "country,asc", activeOnly: true }],
    queryFn: () => UsersApi.listAgents({ page: 0, size: 200, sort: "country,asc", activeOnly: true }),
    select: (page) => (page?.content ?? EMPTY).filter((agent) => agent?.active !== false),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    placeholderData: EMPTY,
  });

  const {
    data: sendersFromApi = EMPTY,
    isLoading: sendersLoading,
    isFetching: sendersFetching,
  } = useQuery({
    queryKey: ["customers", "senders", { country: senderCountryCode, page: 0, size: 200 }],
    queryFn: () => CustomersApi.list({ role: "SENDER", country: senderCountryCode, page: 0, size: 200, sort: "firstName,asc" }),
    select: (page) => page?.content ?? EMPTY,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    enabled: !!senderCountryCode && step >= 2,
    placeholderData: EMPTY,
  });

  const {
    data: recipientsFromApi = EMPTY,
    isLoading: recipientsLoading,
    isFetching: recipientsFetching,
  } = useQuery({
    queryKey: ["customers", "recipients", { country: recipientCountryCode, page: 0, size: 200 }],
    queryFn: () => CustomersApi.list({ role: "RECIPIENT", country: recipientCountryCode, page: 0, size: 200, sort: "firstName,asc" }),
    select: (page) => page?.content ?? EMPTY,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    enabled: !!recipientCountryCode && step >= 5,
    placeholderData: EMPTY,
  });

  const countryAgents = useMemo(() => {
    if (!recipientCountryCode) return EMPTY;
    return allAgents.filter((a) => toAlpha2(a.country) === recipientCountryCode);
  }, [allAgents, recipientCountryCode]);
  const selectedRecipientCountryHasNoAgents =
    Boolean(recipientCountryCode) &&
    !agentsLoading &&
    !agentsFetching &&
    countryAgents.length === 0;

  const blockUI =
    ((step === 1) && agentsLoading && allAgents.length === 0) ||
    ((step >= 2 && step <= 5) && sendersLoading && sendersFromApi.length === 0) ||
    ((step >= 5) && recipientsLoading && recipientsFromApi.length === 0);

  const goToTransactionDashboard = useCallback(() => {
    navigate("/home/transactions", { state: { updatedId: transactionId } });
  }, [navigate, transactionId]);

  const goToAgentRegistration = useCallback((targetStep = step) => {
    navigate("/home/create-user", {
      state: {
        role: "AGENT",
        showBackButton: true,
        backTo: `/home/transfer?step=${targetStep}`,
      },
    });
  }, [navigate, step]);

  const handleBack = useCallback(() => {
    setStepAndUrl(getPrevStep(step, senderType, recipientType));
  }, [step, senderType, recipientType, setStepAndUrl]);

  const activeStageIndex = useMemo(() => {
    let idx = 0;
    wizardStages.forEach((stage, i) => {
      if (step >= stage.step) idx = i;
    });
    return idx;
  }, [step]);

  const activeStageStep = useMemo(() => {
    let currentStage = wizardStages[0]?.step || 1;
    wizardStages.forEach((stage) => {
      if (step >= stage.step) currentStage = stage.step;
    });
    return currentStage;
  }, [step]);

  const formatPersonName = (person) => {
    if (!person) return "";
    if (person.name) return person.name;
    const parts = [person.firstName, person.lastName].filter(Boolean).join(" ").trim();
    return parts;
  };

  const sidebarDraft = useMemo(() => {
    const transfer = transferDetails?.transfer || {};
    return {
      fromCountry: senderCountryName || fromAlpha2(senderCountryCode) || "",
      toCountry: recipientCountryName || fromAlpha2(recipientCountryCode) || "",
      senderName: formatPersonName(selectedSender),
      senderPhone: selectedSender?.phone || "",
      recipientName: formatPersonName(selectedRecipient),
      recipientCity: selectedRecipient?.city || selectedRecipient?.cityName || "",
      amount: transfer.amount,
      sendCurrency: transfer.sendCurrency,
      receivedAmount: transfer.receivedAmount,
      receiveCurrency: transfer.receiveCurrency,
      agentName: [transfer.agentFirstName, transfer.agentLastName].filter(Boolean).join(" ").trim(),
    };
  }, [
    recipientCountryCode,
    recipientCountryName,
    selectedRecipient,
    selectedSender,
    senderCountryCode,
    senderCountryName,
    transferDetails,
  ]);

  const handleSidebarNavigate = useCallback(
    (targetStep) => {
      if (targetStep >= step) return;
      setStepAndUrl(targetStep);
    },
    [setStepAndUrl, step]
  );

  if (blockUI) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <FadeLoader color="#fff" />
      </div>
    );
  }

  return (
    <div className="py-4">
      <section className="ui-panel">
        <div className="ui-panel__header d-lg-none">
          <nav aria-label="Transfer-steps" className="mb-0">
            <div className="d-flex align-items-center flex-wrap gap-2 small">
              {wizardStages.map((stage, idx) => {
                const isActive = idx === activeStageIndex;
                const isClickable = idx < activeStageIndex;
                const textClass = isActive ? "fw-semibold text-primary" : isClickable ? "text-body" : "text-body-secondary";
                const handleClick = () => {
                  if (isClickable) {
                    setStepAndUrl(stage.step);
                  }
                };
                const Element = isClickable ? "button" : "span";
                return (
                  <React.Fragment key={stage.label}>
                    <Element
                      type={isClickable ? "button" : undefined}
                      className={
                        isClickable
                          ? `btn btn-link p-0 m-0 text-decoration-none ${textClass}`
                          : textClass
                      }
                      onClick={isClickable ? handleClick : undefined}
                    >
                      {stage.label}
                    </Element>
                    {idx < wizardStages.length - 1 && (
                      <span className="text-body-tertiary">{">"}</span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </nav>
        </div>

        <div className="row g-4">
          <div className="col-12 col-lg-8 col-xl-8 p-0">
            <div className="d-flex flex-column gap-3">
              {step === 1 && (
                <section className="ui-panel">
                  <header className="ui-panel__header">
                    <h3 className="mb-3 ui-title">Select Countries</h3>
                  </header>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">From</label>
                    <CountrySelect
                      value={senderCountryCode}
                      placeholder="Select sender country"
                      onChange={(code) => {
                        const iso = toAlpha2(code);
                        setSenderCountryCode(iso);
                        setSenderCountryName(fromAlpha2(iso));
                        setSelectedSender(null);
                      }}
                      showFlagInValue
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">To</label>
                    <CountrySelect
                      value={recipientCountryCode}
                      placeholder="Select recipient country"
                      onChange={(code) => {
                        const iso = toAlpha2(code);
                        setRecipientCountryCode(iso);
                        setRecipientCountryName(fromAlpha2(iso));
                        setSelectedRecipient(null);
                      }}
                      isLoading={agentsFetching && !agentsLoading}
                      showFlagInValue
                    />
                  </div>

                  {selectedRecipientCountryHasNoAgents && (
                    <div className="alert alert-warning d-flex flex-column gap-3">
                      <div className="mb-0">
                        No payout agent is available in {recipientCountryName || fromAlpha2(recipientCountryCode)}.
                        Add an agent for this country before creating the transfer.
                      </div>
                      <button
                        type="button"
                        className="btn btn-warning px-4 align-self-start"
                        onClick={() => goToAgentRegistration(1)}
                      >
                        Add Agent
                      </button>
                    </div>
                  )}

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-primary w-100"
                      disabled={!senderCountryCode || !recipientCountryCode}
                      onClick={() => setStepAndUrl(2)}
                    >
                      Continue
                    </button>
                  </div>
                </section>
              )}

              {step === 2 && (
                <ChoosePersonOption
                  type="Sender"
                  onChoose={(type) => {
                    setSenderType(type);
                    setStepAndUrl(type === "existing" ? 3 : 4);
                  }}
                  onBack={handleBack}
                  isExisting={sendersFromApi.length > 0}
                  existingLoading={sendersLoading || sendersFetching}
                />
              )}

              {step === 3 && (
                <SearchListStep
                  data={sendersFromApi}
                  searchedTerm={deferredSenderTerm}
                  setSearchedTerm={setSearchSenderTerm}
                  onSelect={(person) => {
                    const code = toAlpha2(person.country);
                    setSelectedSender({
                      ...person,
                      country: code,
                      countryName: fromAlpha2(code),
                    });
                    setStepAndUrl(5);
                  }}
                  onBack={handleBack}
                  isFetching={sendersFetching}
                />
              )}

              {step === 4 && (
                <PersonForm
                  title="Sender Info"
                  lockOnCountryName={senderCountryCode}
                  onSubmit={(data) => {
                    const code = senderCountryCode || toAlpha2(data.country);
                    setSelectedSender({
                      ...data,
                      country: code,
                      countryName: fromAlpha2(code),
                    });
                    setStepAndUrl(5);
                  }}
                  onBack={handleBack}
                  step={4}
                />
              )}

              {step === 5 && (
                <ChoosePersonOption
                  type="Recipient"
                  onChoose={(type) => {
                    setRecipientType(type);
                    setStepAndUrl(type === "existing" ? 6 : 7);
                  }}
                  onBack={handleBack}
                  isExisting={recipientsFromApi.length > 0}
                  existingLoading={recipientsLoading || recipientsFetching}
                />
              )}

              {step === 6 && (
                <SearchListStep
                  data={recipientsFromApi}
                  searchedTerm={deferredRecipientTerm}
                  setSearchedTerm={setSearchRecipientTerm}
                  onSelect={(person) => {
                    const code = toAlpha2(person.country);
                    setSelectedRecipient({
                      ...person,
                      country: code,
                      countryName: fromAlpha2(code),
                    });
                    setStepAndUrl(8);
                  }}
                  onBack={handleBack}
                  isFetching={recipientsFetching}
                />
              )}

              {step === 7 && (
                <PersonForm
                  title="Recipient Info"
                  lockOnCountryName={recipientCountryCode}
                  onSubmit={(data) => {
                    const code = recipientCountryCode || toAlpha2(data.country);
                    setSelectedRecipient({
                      ...data,
                      country: code,
                      countryName: fromAlpha2(code),
                    });
                    setStepAndUrl(8);
                  }}
                  onBack={handleBack}
                  step={7}
                />
              )}

              {step === 8 && (
                <TransferDetailsForm
                  senderData={selectedSender}
                  recipientData={selectedRecipient}
                  agents={countryAgents}
                  prefillTransfer={prefillTransfer}
                  onBack={handleBack}
                  onSubmit={(data) => {
                    setTransferDetails(data);
                    setStepAndUrl(9);
                  }}
                  backToUrl={`/home/transfer?step=8`}
                />
              )}

              {step === 9 && (
                <TransferSummary
                  details={transferDetails}
                  onBack={handleBack}
                  onConfirm={(id) => {
                    setTransactionId(id);
                    setStepAndUrl(10);
                  }}
                />
              )}

              {step === 10 && (
                <OnSuccess
                  message="Transfer Submitted Successfully!"
                  details="You can now view it in the transactions dashboard."
                  onDone={() => { clearWizard(); goToTransactionDashboard(); }}
                />
              )}
            </div>
          </div>

          <div className="col-12 col-lg-4 col-xl-4 d-none d-lg-block">
            <TransferSidebar
              stages={wizardStages}
              currentStep={step}
              currentStageStep={activeStageStep}
              completedSteps={new Set(wizardStages.filter((s) => s.step < step).map((s) => s.step))}
              onNavigateToStep={handleSidebarNavigate}
              draft={sidebarDraft}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default TransferRegistration;
